import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import DiagnosisService from "../diagnosis/DiagnosisService";

/**
 * Diagnosis picker used on the Add-medicine form. Single-select — a medicine
 * links to at most one diagnosis (the "For · link to a diagnosed condition"
 * dropdown in the design). Fetches active diagnoses from
 * api-diagnosis-v2-service and lets the user pick one or leave un-linked.
 *
 * Mirrors chiefComplaints/ComplaintPicker in shape, but single-select and
 * with a "Not linked" option to unset a prior link.
 */
const DiagnosisPicker = ({ patientId, value, onChange, disabled }) => {
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new DiagnosisService();
    svc.list(
      patientId,
      (res) => {
        setDiagnoses((res && res.active) || []);
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load diagnoses");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = value
    ? diagnoses.find((d) => d.diagnosisId === value)
    : null;

  const pick = (id) => {
    onChange(id || null);
    setOpen(false);
  };

  return (
    <div className="dx-picker" ref={boxRef}>
      <button
        type="button"
        className="dx-picker-trigger"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
      >
        {selected ? (
          <span className="dx-picker-summary">{selected.diagnosis}</span>
        ) : (
          <span className="dx-picker-empty">Link to a diagnosed condition (optional)</span>
        )}
        <span className="dx-picker-caret" aria-hidden="true">&#9662;</span>
      </button>

      {open && (
        <div className="dx-picker-menu" role="listbox" aria-label="Diagnoses">
          {loading && <div className="dx-picker-empty">Loading…</div>}
          {error && <div className="dx-picker-empty is-error">{error}</div>}
          {!loading && !error && (
            <>
              <label
                className={`dx-picker-opt${!value ? " is-on" : ""}`}
                onClick={() => pick(null)}
              >
                <input type="radio" name="dx-picker-single" checked={!value} readOnly />
                <span className="dx-picker-opt-label">Not linked</span>
              </label>
              {diagnoses.length === 0 && (
                <div className="dx-picker-empty">
                  No active diagnoses. Add one from the Diagnosis section first.
                </div>
              )}
              {diagnoses.map((d) => {
                const on = value === d.diagnosisId;
                return (
                  <label
                    key={d.diagnosisId}
                    className={`dx-picker-opt${on ? " is-on" : ""}`}
                    onClick={() => pick(d.diagnosisId)}
                  >
                    <input
                      type="radio"
                      name="dx-picker-single"
                      checked={on}
                      readOnly
                    />
                    <span className="dx-picker-opt-label">{d.diagnosis}</span>
                    {d.diagnosisCode && d.diagnosisCode.icdCode && (
                      <span className="dx-picker-opt-tag">ICD {d.diagnosisCode.icdCode}</span>
                    )}
                  </label>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

DiagnosisPicker.propTypes = {
  patientId: PropTypes.number.isRequired,
  value: PropTypes.string, // diagnosisId (Mongo ObjectID hex)
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default DiagnosisPicker;
